import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { AddToDeliverablesDialog } from '@/components/deals/AddToDeliverablesDialog';
import { DealFormModal } from '@/components/deals/DealFormModal';
import { DealIcon, Money } from '@/components/deals/DealBits';
import { Money as SharedMoney } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { CreditedTo } from '@/components/documents/CreditedTo';
import { DealStageBadge } from '@/components/StageSwatch';
import { InlineDealFields } from '@/components/deals/InlineDealFields';
import { LineItemFormModal } from '@/components/deals/LineItemFormModal';
import { InvoiceIcon, InvoiceMoney, StatusBadge } from '@/components/invoices/InvoiceBits';
import { QuoteIcon, StageBadge } from '@/components/quotes/QuoteBits';
/*
 * ⚠️ **Converting to a quote OPENS THIS rather than firing the request**, on the
 * owner's instruction. A prefilled form the person can correct before anything
 * exists, not a confirmation over a write that has already been decided.
 *
 * ⚠️ **DO NOT put a ConfirmDialog back in front of it.** The modal IS the
 * confirmation — it names the deal in its title, says in as many words that
 * nothing is created until Convert is pressed, and states what the conversion
 * carries and what it leaves alone. A dialog in front would ask the same
 * question twice and take away the editing that is the whole point of the
 * change. The identical rule is written at `QuoteDetailPage.tsx` over
 * `InvoiceFormModal`, for the identical reason.
 */
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
/*
 * Quote PDF preview (owner 2026-10-25): one host per page; every row of the
 * Quotes panel gets a preview button at every stage, and "Create and preview"
 * on the convert form lands on the new quote with `?preview=1`.
 */
import { PreviewQuotePdfButton, useQuotePdfPreview } from '@/components/quotes/QuotePdfPreview';
import { quotePreviewHref } from '@/lib/quote-pdf-preview';
/*
 * ⚠️ **The INVOICE route opens this for exactly the same reason, since
 * 2026-08-30** — owner's decision, the second half of the instruction above. It
 * replaced a `ConfirmDialog` whose one press created a real financial document
 * with nothing on it editable. **DO NOT put that dialog back in front of it**:
 * the modal IS the confirmation — it names the deal in its title, says nothing
 * is created until the button is pressed, and states what the conversion
 * carries and what it leaves alone — and a dialog would ask the same question
 * twice while taking away the line editing that is the whole point.
 */
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
import { ActivityButton, ActivityDrawer } from '@/components/tasks/ActivityDrawer';
import { ApiError, api } from '@/lib/api';
/*
 * ⚠️ **NOTHING FROM `@/lib/deal-conversion` IS IMPORTED HERE ANY MORE, and the
 * empty import is the change rather than a casualty of it.** This page used to
 * take three things from it, all for the invoice `ConfirmDialog` that no longer
 * exists:
 *
 *  - `CONVERTED_LINES_HAVE_NO_TAX` is **DELETED from that module** — a copied
 *    line takes its tax rate from the company now, so the sentence was false in
 *    the reassuring direction on both routes;
 *  - `companyTaxRateRefusalOf` moved with the refusal it reads: the tax-rate 422
 *    is rendered inside `InvoiceFormModal`, the only surface that can now show
 *    it while KEEPING what the person typed;
 *  - `CONVERSION_LEAVES_THE_DEAL_ALONE` is **DELETED** (owner 2026-10-18): the
 *    deal's stage now auto-moves server-side, to the quote-sent stage when a
 *    quote is created or linked and to the won stage when an invoice is raised
 *    from it (never backwards). Its replacements,
 *    `QUOTE_CONVERSION_MOVES_THE_DEAL` and `INVOICE_CONVERSION_MOVES_THE_DEAL`,
 *    are rendered by the two conversion modals, not by this page. The deal's
 *    LINES are still never written back.
 *
 * Re-importing any of the three here would be the first step back to a second
 * copy of a claim about money on a screen that can no longer act on it.
 */
import { effectiveProbability, percentOfDecimal, sumDecimalStrings } from '@/lib/decimal';
import { dealValue, dealValueIsFromLines } from '@/lib/deals';
import {
  formatDateOnly,
  formatDateTime,
  formatDecimalString,
  pluralise,
} from '@/lib/format';
import { attachmentsSentence } from '@/lib/record-delete';
import { QUOTE_SIGNED_LABEL, quoteSignedLabel } from '@/lib/deal-quote-signed';
import { inclusionLine } from '@/lib/inclusions';
import { denialReason } from '@/lib/permissions';
import { viewerZone } from '@/lib/task-time';
import type {
  Deal,
  DealDeliverableLink,
  DealProduct,
  DealUpdateInput,
  Invoice,
  LineCity,
  LineInclusion,
  LookupSummary,
  Quote,
  UserSummary,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { usePageLayout } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { cityLabel } from '@/lib/use-project-options';
import { useUserOptions } from '@/lib/use-user-options';
import { USER_GROUP_SLUGS, narrowingNote, outsideGroupNote } from '@/lib/user-groups';
import {
  Avatar,
  Badge,
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  type FrozenSide,
  PageHeader,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
/* The record's one editable-in-place field. Imported from the module rather
 * than the barrel because the two result types come with it. */
import {
  InlineField,
  type InlineFieldOption,
  type InlineSaveResult,
} from '@/components/ui/InlineField';

function BackToDeals() {
  return (
    <Link
      to="/deals"
      className="inline-flex items-center gap-1 rounded-sm text-xs text-fg-muted transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
    >
      <ChevronIcon direction="left" />
      All deals
    </Link>
  );
}

function DetailItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-fg-secondary [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

function DetailGroup({
  title,
  columns = 'sm:grid-cols-2 lg:grid-cols-3',
  children,
}: {
  title: string;
  columns?: string;
  children: ReactNode;
}) {
  return (
    <PanelBody className="border-b border-border last:border-b-0">
      <h3 className="mb-3 text-sm font-semibold text-fg">{title}</h3>
      <dl className={`grid gap-x-4 gap-y-3 ${columns}`}>{children}</dl>
    </PanelBody>
  );
}

/**
 * How a member of staff is NAMED, everywhere on this page.
 *
 * ⚠️ `UserSummary.name` is typed `string` and can be `''`. Rendered bare, an
 * empty name reads as a MISSING record rather than as a badly-filled one,
 * which is the worse of the two wrong answers: the reader concludes nobody is
 * assigned. One helper rather than a fallback per call site, so a sentence and
 * a value slot cannot name the same person differently.
 */
function staffLabel(person: UserSummary): string {
  return person.name.trim() || person.email.trim() || `Staff #${person.id}`;
}

/**
 * A member of staff in a value slot. The resolved label also seeds the avatar,
 * which would otherwise draw its initials from nothing.
 */
function StaffValue({ person }: { person: UserSummary | null }) {
  if (!person) return <span className="text-fg-subtle">Unassigned</span>;
  const label = staffLabel(person);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar name={label} size="sm" />
      <span className="truncate">{label}</span>
    </span>
  );
}

/**
 * The account lead as it has to READ: who leads the account, AND which of the
 * two answers that is.
 *
 * `deal.accountLead` is already resolved — this deal's own override when it has
 * one, otherwise its company's. Showing that name alone would present a
 * company's choice as though somebody had made it for this deal, and would
 * leave the empty option in the editor looking like an erasure rather than a
 * revert. So the second line says which state this is, and names the person a
 * revert would restore.
 *
 * `accountLead: null` with no override is a real answer and not a gap: the
 * COMPANY names nobody, and only a company can say that.
 */
/*
 * ⚠️ The line under the name — "Following the company" / "Set on this deal ·
 * company: X" — was removed on the owner's stricter no-helper-text ruling
 * (2026-09-05), matching `accountLeadHint`'s removal from `DealFormModal`.
 * Nothing on screen now distinguishes a deal FOLLOWING its company's lead from
 * one that overrode it with the same person.
 */
function AccountLeadValue({ deal }: { deal: Deal }) {
  const hasCompany = deal.company !== null;

  return (
    <span className="grid min-w-0 gap-0.5">
      {deal.accountLead ? (
        <StaffValue person={deal.accountLead} />
      ) : (
        <span className="text-fg-subtle">
          {hasCompany ? 'None on the company' : 'None'}
        </span>
      )}
    </span>
  );
}

function LinkedValue({ to, name }: { to: string; name: string | undefined }) {
  if (!name) return <span className="text-fg-subtle">Unassigned</span>;
  return (
    <TextLink to={to}>{name}</TextLink>
  );
}

/**
 * The project ONE LINE sells under — not the deal's own.
 *
 * It sits with the product rather than in a column of its own because since
 * migration 067 it is half of what the row **is**: `UNIQUE (deal_id,
 * product_id, project_key)`, so the same product legitimately appears twice on
 * one deal and the project is the only thing telling the two rows apart.
 *
 * ⚠️ **A line's `null` is not the deal header's `null`.** The header project
 * comes through `projects.lookup.ts`, which swallows its own failures and
 * returns `null`, so up there an unset project, a deleted one and an unreachable
 * table are indistinguishable — which is why the header copy refuses to be
 * certain. A line's project is a plain `LEFT JOIN d_projects` on
 * `f_deal_products.project_id`, so `null` here really does mean this line names
 * none. Said quietly rather than as an em dash: no project is the ordinary
 * state, not a gap somebody failed to fill.
 */
function LineProject({ project }: { project: LookupSummary | null }) {
  if (project === null) {
    return <span className="mt-1 block text-xs font-normal text-fg-subtle">No project</span>;
  }
  return (
    <span className="mt-1 block">
      <TextLink
        to={`/projects/${project.id}`}
        className="text-xs font-normal [overflow-wrap:anywhere]"
      >
        {project.name}
      </TextLink>
    </span>
  );
}

/**
 * Every city one line runs in — all of them, never a "+2 more".
 *
 * ⚠️ **The money stays on the line.** `quantity` and `unitPrice` describe the
 * line; the cities only say where it runs. "Campus Outreach ×5 at 10,000 across
 * Bangalore, Mumbai and Delhi" is 50,000, not 150,000, and `lineTotal` is
 * generated by the database — so nothing on this page multiplies by
 * `cities.length`, and a count is not rendered beside the total where it could
 * be read as a multiplier.
 *
 * `[]` renders as **nothing at all**: most of what is sold is not city-specific,
 * and an em dash would read as something somebody failed to fill in.
 *
 * Each city carries its own country because city names repeat across countries;
 * `cityLabel()` is the one place that decides how a city is written out. The
 * array arrives already ordered by the server's per-line `sortOrder` and is
 * **not re-sorted** — that order is curated.
 */
function LineCities({ cities }: { cities: LineCity[] }) {
  if (cities.length === 0) return null;
  return (
    <span className="mt-1.5 flex flex-wrap gap-1">
      {cities.map((city) => (
        <Badge key={city.id} tone="neutral" variant="outline">
          {cityLabel(city)}
        </Badge>
      ))}
    </span>
  );
}

/**
 * The campaign inclusions promised on one line.
 *
 * ⚠️ **Nothing here pluralises.** `inclusionLine()` in `@/lib/inclusions` picks
 * between the two stored forms — `unitSingular` for exactly 1, `unitPlural` for
 * everything else, a bare name when the master records neither — and is
 * imported rather than repeated: the same sentence prints in the line editor,
 * on the quote and on the invoice, and the first thing a client-side pluraliser
 * does is put `1 campuses` on a real document.
 *
 * `[]` renders as nothing — not "None", not a dash. A line promising no extras
 * is the usual state, and labelling it turns the usual state into a finding.
 */
function LineInclusions({ inclusions }: { inclusions: LineInclusion[] }) {
  if (inclusions.length === 0) return null;
  const ordered = [...inclusions].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  return (
    <div className="mt-1.5">
      <span className="block text-2xs font-medium text-fg-muted">
        Campaign inclusions
      </span>
      <ul className="mt-0.5 grid gap-0.5 text-xs font-normal text-fg-muted">
        {ordered.map((inclusion) => (
          <li key={inclusion.id} className="[overflow-wrap:anywhere]">
            {inclusionLine(inclusion)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What to call one line when it is named away from the table — a button's
 * accessible name, a confirmation title.
 *
 * **The product name alone is not unique on a deal** (migration 067). Two lines
 * may carry the same product under different projects, and "Remove Summer
 * Immersion Programme?" against two identical-looking rows is a coin toss. Two
 * project-less lines for one product are refused by the server, so product +
 * project is unambiguous wherever it renders.
 */
function lineLabel(line: DealProduct): string {
  return line.project ? `${line.product.productName} — ${line.project.name}` : line.product.productName;
}

function DetailsSkeleton() {
  return (
    <Panel>
      <PanelHeader title="Deal details" />
      <PanelBody>
        <div className="grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 9 }, (_, i) => (
            <div key={i} className="grid gap-1.5">
              <Skeleton className="h-2.5 w-20" />
              <Skeleton className="h-3.5 w-32" />
            </div>
          ))}
        </div>
      </PanelBody>
    </Panel>
  );
}

/* ── The basket's columns ──────────────────────────────────────────────────
 *
 * ONE ordered array, four consumers: the header, the body row, the skeleton and
 * the width table all walk it. Before this each of the three cell banks was
 * written out longhand, so a column could exist in two of them and the only
 * symptom was a skeleton that jumped sideways when the lines landed.
 *
 * ⛔ **The three LINE-ITEM GRIDS are deliberately not this.** `QuoteLineItems-
 * Table`, `InvoiceLineItemsTable` and `DealLineItemsTable` are editors: every
 * column is an input, and the Item cell hosts the only controls for product,
 * project, cities, regions and inclusions. Hiding a column there removes the
 * only way to set a value that reaches a customer's invoice. This table is the
 * READ-ONLY view of the same basket, which is why a column manager belongs on
 * it and not on them.
 */
const PRODUCT_KEY = 'product';
const ACTIONS_KEY = 'actions';

const BASKET_COLUMN_KEYS: readonly string[] = [
  PRODUCT_KEY,
  'quantity',
  'unitPrice',
  'discount',
  'lineTotal',
  ACTIONS_KEY,
];

/**
 * Neither of these two may be hidden or moved.
 *
 * ⚠️ *SUPERSEDED: "Neither of these two may be hidden, moved **or frozen**."*
 * **LOCKED SAYS NOTHING ABOUT WHICH EDGE A COLUMN SITS AT**, and reading it as
 * though it did is what made `product` unfreezable — see the note at
 * `frozenSideOf` below. Product is the column that identifies the line, which is
 * exactly the column a reader wants held at the left while the money scrolls
 * under it. It is freezable again; **Actions is the one that is not, and it is
 * excluded for being `pinned` rather than for being locked.**
 *
 * ⚠️ **Module-level, not an inline literal.** `useColumnLayout` memoises the
 * locked set on the array's identity, so a fresh array every render would
 * rebuild it every render.
 */
const BASKET_LOCKED_KEYS: readonly string[] = [PRODUCT_KEY, ACTIONS_KEY];

/**
 * The actions lane is `pinned`, which IS `frozen="right"` at offset zero. That
 * has to be declared to all three of `useColumnLayout`, `ColumnManager` and
 * `useFrozenOffsets`, and each ignores it for a different reason:
 *
 * - **`useColumnLayout`** refuses `setFrozen` on it, so a stored layout can never
 *   put a second, conflicting position rule on a cell that is already
 *   `sticky right-0` — a button that reads as pressed while nothing moves.
 * - **`ColumnManager`** omits its freeze controls entirely rather than disabling
 *   them: that edge is not the reader's to choose, so there is no state a
 *   disabled control would be explaining.
 * - **`useFrozenOffsets`** counts its WIDTH, or the first column the reader
 *   freezes to the right is handed offset 0 as well and the two stack, each fully
 *   opaque, with nothing to show for it but a column that appears to have gone.
 *
 * ⛔ **This is NOT `BASKET_LOCKED_KEYS` and must never be conflated with it.**
 */
const BASKET_PINNED_KEYS: readonly string[] = [ACTIONS_KEY];

const BASKET_LABELS: Record<string, string> = {
  [PRODUCT_KEY]: 'Product',
  quantity: 'Qty',
  unitPrice: 'Unit price',
  discount: 'Discount',
  lineTotal: 'Line total',
  [ACTIONS_KEY]: 'Actions',
};

/**
 * The px each column contributes to the table's minimum width.
 *
 * 🔴 **THIS REPLACES A `min-w-[760px]` LITERAL WRITTEN OUT TWICE, AND THE REASON
 * IT HAD TO IS THE POINT: a fixed floor does not shrink when a column is
 * hidden.** Hide three of six and the table still claims 760px, so the reader
 * who hid them to stop scrolling sideways goes on scrolling sideways.
 *
 * **The six add up to exactly 760**, which is the floor this table carried
 * before the manager arrived — showing every column must leave the table
 * precisely where it was, or adopting a view control silently re-lays out a
 * page nobody asked to change. `product` has no width class of its own (it
 * takes what is left), so its entry is the 216px the old literal implicitly
 * reserved for it once the five fixed columns are subtracted.
 */
const BASKET_COLUMN_WIDTHS: Record<string, number> = {
  [PRODUCT_KEY]: 216,
  quantity: 80,
  unitPrice: 128,
  discount: 96,
  lineTotal: 144,
  [ACTIONS_KEY]: 96,
};

/** The actions lane narrows when it holds no controls — as its header already does. */
function basketColumnWidth(key: string, showActions: boolean): number {
  if (key === ACTIONS_KEY && !showActions) return 64;
  return BASKET_COLUMN_WIDTHS[key] ?? 0;
}

function basketHeadClass(key: string, showActions: boolean): string | undefined {
  switch (key) {
    case 'quantity':
      return 'w-20';
    case 'unitPrice':
      return 'w-32';
    case 'discount':
      return 'w-24';
    case 'lineTotal':
      return 'w-36';
    case ACTIONS_KEY:
      return showActions ? 'w-24' : 'w-16';
    default:
      return undefined;
  }
}

function basketCellClass(key: string): string | undefined {
  switch (key) {
    case 'quantity':
    case 'discount':
      return 'tabular';
    case 'lineTotal':
      return 'font-medium text-fg';
    default:
      return undefined;
  }
}

/** The four money-and-number cells. Product and Actions are not here: one is a
 *  `<TH scope="row">` and the other a lane of controls. */
function BasketValue({ column, line }: { column: string; line: DealProduct }) {
  switch (column) {
    case 'quantity':
      return <>{line.quantity}</>;
    case 'unitPrice':
      return <Money value={line.unitPrice} emptyLabel="No price on this line" />;
    case 'discount':
      /* A percentage, never a currency amount. */
      return Number(line.discount) === 0 ? (
        <span className="text-fg-subtle">—</span>
      ) : (
        <>{`${line.discount}%`}</>
      );
    case 'lineTotal':
      return <Money value={line.lineTotal} />;
    default:
      return null;
  }
}

interface FrozenModel {
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}

/**
 * ⚠️ **Returns an EMPTY object for an unfrozen column, not `frozen: undefined`
 * with an offset beside it.** `TH`/`TD` only emit a `left`/`right` when `frozen`
 * is set, and an unfrozen cell that became a positioned one would paint over the
 * table's collapsed borders for no reason at all.
 */
function frozenPropsFor(
  key: string,
  { frozenSideOf, frozenOffsets }: FrozenModel,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const frozen = frozenSideOf(key);
  if (frozen === undefined) return {};
  return { frozen, frozenOffset: frozenOffsets[key] ?? 0 };
}

function BasketSkeleton({
  columnOrder,
  showActions,
  frozen,
}: {
  columnOrder: readonly string[];
  showActions: boolean;
  frozen: FrozenModel;
}) {
  return (
    <TBody>
      {Array.from({ length: 3 }, (_, i) => (
        <TR key={i}>
          {columnOrder.map((key) =>
            key === PRODUCT_KEY ? (
              /* Two bars: the product cell carries a second line, the project.
                 City and inclusion blocks are not drawn — they are absent on
                 most lines, and a skeleton that promises them makes the
                 ordinary result look like something went missing.

                 ⚠️ It freezes with the column too, now that `product` can be
                 frozen: a skeleton whose first column scrolled away while the
                 loaded table's held still would look like the setting only
                 taking effect once the data arrived. A `TD` rather than the
                 `TH scope="row"` the real row uses — nothing here identifies a
                 record — and the two take the same body literals either way. */
              <TD key={key} {...frozenPropsFor(key, frozen)}>
                <Skeleton className="h-3 w-48" />
                <Skeleton className="mt-1.5 h-2.5 w-28" />
              </TD>
            ) : key === ACTIONS_KEY ? (
              <TD key={key} numeric pinned>
                <Skeleton className={showActions ? 'ml-auto h-3 w-12' : 'ml-auto h-3 w-4'} />
              </TD>
            ) : (
              <TD key={key} numeric {...frozenPropsFor(key, frozen)}>
                <Skeleton className={SKELETON_BAR[key] ?? 'ml-auto h-3 w-12'} />
              </TD>
            ),
          )}
        </TR>
      ))}
    </TBody>
  );
}

/**
 * The bar widths the longhand skeleton used, kept per column so hiding one
 * cannot shift the rest. Whole class strings rather than a `cx` join: the
 * classes are literals Tailwind can see, and this file imports no joiner.
 */
const SKELETON_BAR: Record<string, string> = {
  quantity: 'ml-auto h-3 w-8',
  unitPrice: 'ml-auto h-3 w-16',
  discount: 'ml-auto h-3 w-10',
  lineTotal: 'ml-auto h-3 w-20',
};

/*
 * ⚠️ **The `converting` flag and the synchronous double-click guard must never
 * be "tidied" away.** Both conversion endpoints are repeatable and answer **no
 * 409**, so a second click in one tick raises a second real document — and on
 * the invoice route that is a second real financial document.
 *
 * The refusal renderer covers a single coded 422 that routes to **another
 * record** — see `src/lib/deal-conversion.ts`.
 */

export function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  /*
   * ⚠️ Read for ONE parameter — `?create=quote|invoice`, the deals list's row
   * "+" menu handing this page an action to open. See the effect below.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const { ability, user } = useAuth();

  /*
   * Per-person section layout (layout manager, 2026-10-20). Called above every
   * early return so the hook order never changes. The defaults are today's
   * stack exactly: four full-width panels in this order. ⚠️ Ids are stored per
   * user — never rename one.
   */
  const layout = usePageLayout('deal', [
    { id: 'details', label: 'Deal details' },
    { id: 'products', label: 'Products' },
    { id: 'quotes', label: 'Quotes raised from this deal' },
    { id: 'invoices', label: 'Invoices raised from this deal' },
  ]);

  /*
   * The read gate, computed here because the return that uses it has to sit
   * below every hook. See that return, further down, for the reasoning.
   */
  const canReadModule = ability.canRead('deals');
  const dealId = Number(id);
  const validId = Number.isInteger(dealId) && dealId > 0;

  const [deal, setDeal] = useState<Deal | null>(null);
  const [dealError, setDealError] = useState<string | null>(null);
  /*
   * True when the deal read was REFUSED rather than failed — a 403, or a 404.
   * Since 2026-10-01 the server enforces `scope: 'own'` on `deals.read`, so a
   * reader whose set says `own` is refused another person's deal by URL, and the
   * server may answer 404 to hide that the deal exists. Both render as one
   * no-access state: never "couldn't be loaded" with a retry that will be
   * refused identically, and never a claim the deal does not exist.
   */
  const [dealNoAccess, setDealNoAccess] = useState(false);
  const [dealLoading, setDealLoading] = useState(true);

  const [lines, setLines] = useState<DealProduct[] | null>(null);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [linesLoading, setLinesLoading] = useState(true);

  /*
   * The quotes already raised FROM this deal.
   *
   * A deal carries no pointer to them — the link is a column on the QUOTE — so
   * this is a filtered list on `dealId`, not an embed, and a quote raised from
   * the header button lands here.
   *
   * It is fetched on every load rather than on demand, because it is the thing
   * that stops a fifth quote being raised by accident: nothing refuses a
   * repeat, so the record of what already exists is the only guard there is.
   *
   * `perPage: 100` because the panel renders them all; `meta.total` is kept
   * separately so the copy is honest if a deal ever exceeds that.
   */
  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [quotesTotal, setQuotesTotal] = useState<number | null>(null);
  const [quotesError, setQuotesError] = useState<string | null>(null);
  const [quotesLoading, setQuotesLoading] = useState(true);

  /*
   * The invoices already raised FROM this deal — the twin of the quotes above,
   * with more at stake. `POST /deals/{id}/invoices` is repeatable and answers no
   * 409, so nothing on the server stops a fourth invoice and this list is the
   * only thing that does; an invoice is also the document that demands money.
   *
   * The link is a column on the INVOICE (`d_invoices.deal_id`), so this is a
   * filtered list rather than an embed.
   */
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [invoicesTotal, setInvoicesTotal] = useState<number | null>(null);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [lineForm, setLineForm] = useState<{ line?: DealProduct } | null>(null);
  const [pendingLine, setPendingLine] = useState<DealProduct | null>(null);
  const [pendingDeal, setPendingDeal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  /*
   * ── Raising a quote or an invoice FROM this deal ─────────────────────────
   *
   * ✅ **THE TWO ROUTES SHARE A SHAPE AGAIN — and it is the FORM's, not the
   * dialog's.** Both open a prefilled, editable modal that posts nothing until
   * Save: `QuoteFormModal convertFrom={deal}` and, since 2026-08-30,
   * `InvoiceFormModal convertFromDeal={deal}` (owner's decision, the second half
   * of the same instruction). This page holds a boolean for each and nothing
   * else about the conversion.
   *
   * ⚠️ **EVERYTHING THIS PAGE USED TO OWN FOR THE INVOICE ROUTE IS GONE, and
   * the deletions are the point rather than tidying.** *SUPERSEDED, the whole
   * block: "`pendingConvert` is the INVOICE confirmation and nothing else … The
   * invoice route is deliberately UNCHANGED — a document that demands money is
   * not one to alter on inference from a decision that named quotes … The error
   * is kept as the ApiError itself rather than as a string, because the one
   * refusal worth routing sends the reader to ANOTHER RECORD."* With it went
   * `pendingConvert`, `converting`, `convertingRef`, `convertError`,
   * `raiseInvoice()`, `convertFailure()` and `alreadyRaised`.
   *
   * ⚠️ **The reasoning each of them carried has NOT been discarded — it moved
   * inside the form, which is the only place that can now act on it**, and
   * leaving a second copy here is how two screens come to word one refusal two
   * ways:
   *
   *  - the **synchronous double-click guard** is `submitLock` in the form. It
   *    still matters for exactly the reason recorded here: `POST
   *    /deals/{id}/invoices` answers **no 409**, so a second click before React
   *    re-renders raises a SECOND real financial document nothing can withdraw.
   *  - the **`COMPANY_HAS_NO_TAX_RATE` refusal** is `dealConvertFailure` there,
   *    which renders the server's two sentences and a link to the company —
   *    never a retry — and, because the check runs before the transaction opens,
   *    **keeps everything the person typed** instead of closing over it.
   *  - **what has already been raised** is on the page itself, in the Invoices
   *    panel below, where it is visible without opening anything.
   */
  /**
   * The prefilled Convert-to-quote form is open.
   *
   * ⚠️ **A boolean rather than a held `Deal`, because the deal is right here.**
   * Snapshotting the record into state would let this page edit the deal
   * underneath an open modal and leave the form seeded from the version before
   * the edit — and the seed decides what the follow-up PATCH sends, so the
   * divergence would be written rather than merely displayed.
   */
  const [convertingToQuote, setConvertingToQuote] = useState(false);
  /** The prefilled Raise-an-invoice form is open. Same shape, same reason. */
  const [raisingInvoice, setRaisingInvoice] = useState(false);

  /*
   * The staff directory for the one field this page edits in place, the
   * account-lead OVERRIDE. One page at the API's maximum, filtered locally by
   * the combobox; `hasMore` is surfaced as a footnote rather than silently
   * hiding people.
   *
   * ⚠️ NARROWED to the account-lead group, REVERSING the note that stood here.
   * It said `assertLeadsInGroups` ran in the companies and contacts services
   * only and that deals checked existence alone; that was measured wrong.
   * `assertInGroup('accountLeadId', …)` runs in the deals service too, so this
   * control was over-offering into a live 422 — pick a colleague, save, collect
   * a refusal.
   *
   * ⚠️ **A group master that did not load narrows NOTHING**, by construction:
   * `narrowStaffToGroup` finds no row for the slug — 404, failed read, slug
   * absent — and returns the whole directory with `narrowed: false`. An
   * unreadable master is not evidence that nobody may lead a deal, and the
   * server's 422 names who may, which beats a picker guessed empty. That flag
   * also silences `narrowingNote`/`outsideGroupNote` below, so nothing claims a
   * restriction we could not confirm. See the fuller note in `DealFormModal`.
   */
  const { loading: usersLoading, byGroup } = useUserOptions(true, true);
  const leadStaff = byGroup[USER_GROUP_SLUGS.accountLead];
  /** Two quick account-lead writes can resolve out of order; only the newest may land. */
  const accountLeadWriteSeq = useRef(0);

  /*
   * Tasks and notes, and the one place in this API where nothing is gated: the
   * task and note routers carry NO role guard at all, so `viewer` may add both.
   * Nothing below consults `ability`, deliberately.
   *
   * Both live in `ActivityDrawer`, behind one button in the header — it owns the
   * fetching, the completion toggle, the delete and the note composer, so this
   * page holds nothing but whether the sheet is open.
   */
  const viewerId = user?.id ?? null;
  const viewerTimeZone = viewerZone(user?.timezone);
  const [activityOpen, setActivityOpen] = useState(false);

  /*
   * What the delete confirmation is allowed to promise, read only when that
   * dialog opens. The drawer does not report its writes back here, so a figure
   * cached at page load would be stale in exactly the direction that
   * understates what is about to be destroyed.
   */
  const [attachmentTotals, setAttachmentTotals] = useState<{
    tasks: number | null;
    notes: number | null;
    systemNotes: number;
  }>({ tasks: null, notes: null, systemNotes: 0 });

  /*
   * Writes take a write role, not the admin tier — `requireRole(...WRITE_ROLES)`
   * on the deal and deal-product routes. Deletes are the exception: every DELETE
   * in the API is admin-tier. `permissions.ts` reconciles the two.
   */
  const canCreate = ability.canCreate('deals');
  const canUpdate = ability.canUpdate('deals');
  const canDelete = ability.canDelete('deals');
  const showLineActions = canUpdate || canDelete;

  /* ── The basket's column layout ───────────────────────────────────────── */

  const basketLayout = useColumnLayout(
    'deal-basket',
    BASKET_COLUMN_KEYS,
    BASKET_LOCKED_KEYS,
    /* The actions lane — see `BASKET_PINNED_KEYS`. Passing it here is what keeps
       `setFrozen` off a `pinned` cell WITHOUT taking freeze away from `product`,
       which is what the locked set would have done. */
    BASKET_PINNED_KEYS,
  );
  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /**
   * The visible columns in render order, with Product forced first and Actions
   * forced last.
   *
   * ⚠️ **`ColumnManager` locks hiding and MOVING a locked key, but it does not
   * stop another column being moved PAST one** — "Move down" on the
   * second-to-last row is enabled, and one more press puts that column after
   * Actions. Actions carries `pinned`, which is hard-coded `right-0`, so it
   * would then paint over whatever had overtaken it; and Product is the row
   * header that identifies the line, which reads as a stray column the moment
   * something precedes it. Both are locked, so both are always visible and can
   * be re-inserted unconditionally rather than filtered for.
   */
  const columnOrder = useMemo(
    () => [
      PRODUCT_KEY,
      ...basketLayout.visibleKeys.filter((key) => key !== PRODUCT_KEY && key !== ACTIONS_KEY),
      ACTIONS_KEY,
    ],
    [basketLayout.visibleKeys],
  );

  /**
   * What the CELLS are told, which is not quite what the offsets are measured
   * from.
   *
   * ⛔ **A PINNED key never reports a side.** Actions is `pinned`, so handing it
   * a `frozen` as well would put two conflicting position rules on one cell and
   * the winner is whichever the browser resolves last rather than a decision
   * anybody made. `useColumnLayout` refuses `setFrozen` on it and `ColumnManager`
   * offers no control for it, so this is unreachable through the UI — it is the
   * guard behind those, for a layout edited anywhere other than the manager.
   *
   * 🔴 **IT USED TO BLANK `BASKET_LOCKED_KEYS`, WHICH MADE `product` UNFREEZABLE
   * — THE BUG THIS PASS EXISTS TO CLOSE.** *SUPERSEDED, and note it was right
   * about the symptom and wrong about where the fix belonged: "Product is a
   * `<TH scope="row">` in the BODY, and `TH`'s frozen classes carry the HEADER
   * tint (`bg-surface-sunken`), so a frozen product cell would paint every row's
   * first cell as though it were a header."* **`TH` decides that on `scope` now**
   * — a `scope="row"` cell takes `FROZEN_TD_*`, the body literals, with the
   * redrawn row separator and the lower rung. The row-header case is the COMMON
   * one for a freeze-left column, so it was fixed where every table gets it
   * rather than by withholding the feature here.
   */
  /* Destructured so the dependency is a plain identifier rather than a member
     expression, which `oxlint` cannot see through — it read `[basketLayout
     .frozenSideOf]` as a missing `basketLayout` and warned on every build. Same
     shape `CatalogPage` already uses, and the semantics are identical: the
     controller memoises this callback, so its identity moves only when the
     frozen edges really do. */
  const { frozenSideOf: layoutFrozenSideOf } = basketLayout;
  const frozenSideOf = useCallback(
    (key: string): FrozenSide | undefined =>
      BASKET_PINNED_KEYS.includes(key) ? undefined : layoutFrozenSideOf(key),
    [layoutFrozenSideOf],
  );

  /* The fourth argument is the actions lane — see `BASKET_PINNED_KEYS`. */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    columnOrder,
    frozenSideOf,
    BASKET_PINNED_KEYS,
  );

  const frozen = useMemo<FrozenModel>(
    () => ({ frozenSideOf, frozenOffsets }),
    [frozenSideOf, frozenOffsets],
  );

  /** A number, never a computed `min-w-[…]` class — Tailwind scans source text,
   *  so an interpolated class emits no rule and the floor silently vanishes. */
  const basketMinWidth = useMemo(
    () => columnOrder.reduce((sum, key) => sum + basketColumnWidth(key, showLineActions), 0),
    [columnOrder, showLineActions],
  );

  /** Why each locked column is locked, in the reader's own terms — one of the
   *  four kinds of sentence the no-helper-text rule keeps, because a bare
   *  `disabled` makes a locked column and a broken switch identical. */
  const basketLockReason = useCallback((key: string): string | undefined => {
    if (key === PRODUCT_KEY) {
      return 'The product names the line, and its project, cities and campaign inclusions appear nowhere else on this page';
    }
    if (key === ACTIONS_KEY) {
      return 'The per-line actions lane — nothing else on this page edits or removes a line';
    }
    return undefined;
  }, []);

  /*
   * Raising a quote is CREATING A QUOTE, so it is gated as `QuotesPage` gates
   * its own New quote button — `ability.canCreate('quotes')`.
   *
   * ⚠️ Not `canConvertQuote()`. `convert` exists on ONE module in the whole
   * catalogue, `quotes.convert`, and it means quote→invoice; there is no
   * `deals.convert` row to hold, so asking for one would gate this on a
   * permission nobody can ever be granted.
   */
  const canCreateQuote = ability.canCreate('quotes');
  /* The invoice equivalent: this button produces a new INVOICE record, so
   * `invoices.create` says who may produce one — not `quotes.convert`, which is
   * quote→invoice and a different act entirely. */
  const canCreateInvoice = ability.canCreate('invoices');

  /*
   * ── Adding this deal's lines to the client deliverables tracker ──────────
   *
   * Each line becomes one STANDALONE deliverable, derived server-side from the
   * line — nothing is typed. The button is WITHHELD (not disabled) without
   * `deliverables.create`, and withheld on a deal with no lines.
   *
   * `deliverableLinks` says which lines already back a deliverable, and is what
   * the "All lines added" label is computed from. ⚠️ **A FAILED read is `null`
   * and must never claim "All lines added"** — the button stays enabled and the
   * server's `already-added` refusal speaks for any line that is.
   */
  const canCreateDeliverables = ability.canCreate('deliverables');
  const [deliverableLinks, setDeliverableLinks] = useState<DealDeliverableLink[] | null>(null);
  const [addingDeliverables, setAddingDeliverables] = useState(false);

  const loadDeliverableLinks = useCallback(
    (signal?: AbortSignal) => {
      if (!validId || !canCreateDeliverables) return Promise.resolve();
      return api
        .listDealDeliverableLinks(dealId, signal)
        .then((result) => {
          setDeliverableLinks(result.data);
        })
        .catch(() => {
          if (signal?.aborted) return;
          setDeliverableLinks(null);
        });
    },
    [dealId, validId, canCreateDeliverables],
  );

  const loadDeal = useCallback(
    (signal?: AbortSignal) => {
      if (!validId) return Promise.resolve();
      setDealLoading(true);
      return api
        .getDeal(dealId, signal)
        .then((result) => {
          setDeal(result.data);
          setDealError(null);
          setDealNoAccess(false);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          // Split on the STATUS, never on the message.
          setDealNoAccess(
            caught instanceof ApiError && (caught.status === 403 || caught.status === 404),
          );
          setDealError(caught instanceof Error ? caught.message : 'Failed to load the deal');
        })
        .finally(() => {
          if (!signal?.aborted) setDealLoading(false);
        });
    },
    [dealId, validId],
  );

  const loadLines = useCallback(
    (signal?: AbortSignal) => {
      if (!validId) return Promise.resolve();
      setLinesLoading(true);
      // Not paginated: the whole basket arrives at once, which is what totalling
      // it requires anyway.
      return api
        .listDealProducts(dealId, signal)
        .then((result) => {
          setLines(result.data);
          setLinesError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setLinesError(caught instanceof Error ? caught.message : 'Failed to load the products');
        })
        .finally(() => {
          if (!signal?.aborted) setLinesLoading(false);
        });
    },
    [dealId, validId],
  );

  const loadQuotes = useCallback(
    (signal?: AbortSignal) => {
      if (!validId) return Promise.resolve();
      setQuotesLoading(true);
      return api
        .listQuotes({ dealId, perPage: 100 }, signal)
        .then((result) => {
          setQuotes(result.data);
          setQuotesTotal(result.meta.total);
          setQuotesError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setQuotes(null);
          setQuotesTotal(null);
          setQuotesError(caught instanceof Error ? caught.message : 'Failed to load the quotes');
        })
        .finally(() => {
          if (!signal?.aborted) setQuotesLoading(false);
        });
    },
    [dealId, validId],
  );

  /* After Mark Sent / Send to Xero in the viewer, the stage column is stale, and
     so is the deal: its stage and "Quote signed" are derived from its quotes. */
  const quotePdfPreview = useQuotePdfPreview({
    onChanged: () => {
      void loadQuotes();
      void loadDeal();
    },
  });

  const loadInvoices = useCallback(
    (signal?: AbortSignal) => {
      if (!validId) return Promise.resolve();
      setInvoicesLoading(true);
      return api
        .listInvoices({ dealId, perPage: 100 }, signal)
        .then((result) => {
          setInvoices(result.data);
          setInvoicesTotal(result.meta.total);
          setInvoicesError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setInvoices(null);
          setInvoicesTotal(null);
          setInvoicesError(
            caught instanceof Error ? caught.message : 'Failed to load the invoices',
          );
        })
        .finally(() => {
          if (!signal?.aborted) setInvoicesLoading(false);
        });
    },
    [dealId, validId],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void loadDeal(controller.signal);
      void loadLines(controller.signal);
      void loadQuotes(controller.signal);
      void loadInvoices(controller.signal);
      void loadDeliverableLinks(controller.signal);
    }, 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [loadDeal, loadLines, loadQuotes, loadInvoices, loadDeliverableLinks]);

  /*
   * ── `?create=quote|invoice`: the deals list handing this page an action ──
   *
   * The row "+" menu on `DealsPage` navigates HERE rather than to a create
   * form, because both conversion surfaces need the whole `Deal` and a list row
   * does not carry one — the reasoning is written out at the top of that file.
   * All this does is press the button the person chose there.
   *
   * ⚠️ **CONSUMED EXACTLY ONCE, AND STRIPPED FROM THE URL — `replace`, not
   * push, so Back does not re-trigger it.** Left in place, a refresh or a Back
   * re-opens the form; on the INVOICE side that form raises a document that
   * demands money, and `POST /deals/{id}/invoices` answers no 409, so a
   * re-opened form is a second real financial document a few presses away. The
   * guard below re-arms only once the parameter is actually gone, which is also
   * what makes React's double-invoked effects in development harmless.
   *
   * ⚠️ **IT OPENS THE FORM; IT DOES NOT SAVE.** Nothing here posts anything —
   * both branches only set a boolean. A URL that raised an invoice on navigation
   * is a URL somebody can bookmark, share, or hit twice.
   *
   * ⚠️ **BOTH BRANCHES NOW OPEN A MODAL** and this comment used to say the
   * invoice one opened a confirmation. It did until 2026-08-30; the owner's
   * decision made deal→invoice an editable prefilled form like deal→quote, so
   * there is no `ConfirmDialog` on this page for either conversion any more.
   *
   * ⚠️ **A URL IS NOT AUTHORISATION.** Each half is re-checked against the same
   * module the menu gated on — `quotes.create`, `invoices.create`, separately,
   * because a set may hold one without the other. An unpermitted or
   * unrecognised value opens NOTHING, silently: there is no refusal to render,
   * since the person never chose anything on this page.
   *
   * ⚠️ Nothing here writes to the deal; this is only a trigger. (The
   * conversion it opens does move the deal's stage server-side since
   * 2026-10-18; `CONVERSION_LEAVES_THE_DEAL_ALONE` is deleted.)
   */
  const createParamConsumed = useRef(false);
  useEffect(() => {
    const requested = searchParams.get('create');
    if (requested === null) {
      createParamConsumed.current = false;
      return;
    }
    if (createParamConsumed.current) return;
    createParamConsumed.current = true;

    // Stripped whatever the value was, including one this page does not
    // recognise: a parameter left in the bar is one a reload acts on.
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });

    /*
     * `react(set-state-in-effect)` fires on the two branches below and its
     * usual remedy does not apply. Deriving during render is what `DealsPage`
     * does with `?new=1` — and it works there precisely because that parameter
     * STAYS in the URL. This one must not: it has to be spent and removed, so
     * the open is an event (arriving with the parameter) rather than a function
     * of the URL, and there is no render-time expression that is true once.
     */
    if (requested === 'quote' && canCreateQuote) {
      // oxlint-disable-next-line react/set-state-in-effect
      setConvertingToQuote(true);
      return;
    }
    if (requested === 'invoice' && canCreateInvoice) {
      // oxlint-disable-next-line react/set-state-in-effect
      setRaisingInvoice(true);
    }
  }, [searchParams, setSearchParams, canCreateQuote, canCreateInvoice]);

  /*
   * The read gate, below every hook so the early return cannot change the hook
   * order — and ABOVE the not-found and loading branches, deliberately. A reader
   * who may not see deals must not be told whether this one exists: "Deal not
   * found" over a record that is merely hidden from them is a wrong answer
   * dressed as a real one.
   *
   * ⚠️ **A detail page needs the same gate as its list.** `/deals/:id` open
   * while `/deals` refuses is the same hole one level down, and a detail page is
   * exactly what a stale bookmark or a link in an email points at.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. And **hiding a page
   * is not refusing a request** — `GET /deals/:id` answers the same to
   * everybody, so nothing here may be worded as though the record were
   * protected. This is a view control.
   *
   * No back link: `/deals` answers this reader with the same refusal.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Operations" title="Deals" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  if (!validId) {
    return (
      <>
        <PageHeader eyebrow={<BackToDeals />} title="Deal not found" />
        <Panel>
          <EmptyState
            icon={<DealIcon />}
            title="That isn’t a deal reference"
            description="A deal id is a positive whole number."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/deals')}>
                Back to deals
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  const notFound = dealError !== null && deal === null;

  if (dealLoading && !deal) {
    return (
      <>
        <PageHeader eyebrow={<BackToDeals />} title="Deal" />
        <DetailsSkeleton />
      </>
    );
  }

  if (notFound && dealNoAccess) {
    return (
      <>
        <PageHeader eyebrow={<BackToDeals />} title="Deal" />
        <Panel>
          <EmptyState
            icon={<DealIcon />}
            title="You don’t have access to this deal"
            description="It may not exist, or your permission set may not include it."
            action={
              <Button variant="secondary" size="sm" onClick={() => navigate('/deals')}>
                Back to deals
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageHeader eyebrow={<BackToDeals />} title="Deal" />
        <Panel>
          <EmptyState
            icon={<DealIcon />}
            title="This deal couldn’t be loaded"
            description={dealError}
            action={
              <Button variant="secondary" size="sm" onClick={() => void loadDeal()}>
                Try again
              </Button>
            }
          />
        </Panel>
      </>
    );
  }

  if (!deal) return null;

  const pct = effectiveProbability(deal.stage?.probability);
  /*
   * ⚠️ **`dealValue`, not `deal.amount` — owner's ruling, 2026-09-10.** The
   * deal's value is its line-item total whenever it has lines, so the forecast
   * follows the basket rather than a figure somebody typed before the lines
   * existed. `null` when there is neither value nor stage, which is what the
   * empty label below speaks to.
   */
  const expected = percentOfDecimal(dealValue(deal), pct);
  /*
   * Does the figure on screen come from the LINES while the typed amount is
   * empty? The inline Amount field edits `deal.amount`, so in that one case a
   * reader sees a number above a control that has nothing in it — worth saying
   * out loud rather than leaving them to conclude the save failed.
   */
  const valueFromLines = dealValueIsFromLines(deal);
  /*
   * Sums the server's own per-line totals with decimal string maths and
   * deliberately does not recompute them: the database rounds each line, and
   * summing unrounded lines then rounding once gives a different answer — the
   * database's is the one Xero and the reports will use.
   */
  const basketTotal = sumDecimalStrings((lines ?? []).map((line) => line.lineTotal));

  /*
   * ⚠️ **The whole basket goes to the line editor; never reduce it to a flat
   * list of product ids.** Identity is the **(product, project) pair** —
   * `UNIQUE (deal_id, product_id, project_key)` since migration 067 — so a
   * product already on the deal under project A is a perfectly legal line under
   * project B. Excluding by product alone hides a line the deal is meant to
   * allow, and the picker simply lacks a row, so the person concludes the
   * product does not exist rather than that a rule refused them.
   *
   * Passed unmapped: `DealLineIdentity` is `{id, product, project}`, a
   * structural subset of the read shape. The pairing is decided INSIDE the
   * editor on purpose — the project it must key against is the one selected in
   * the form, not the one the line was loaded with. Two project-less lines for
   * one product are still refused, which is what `project_key`'s stored
   * `COALESCE(project_id, 0)` is for: MySQL compares `NULL`s as distinct in a
   * unique key, so the tighter-looking alternative would let a double-clicked
   * "Add product" bill twice.
   */
  /**
   * Writes the account-lead OVERRIDE and reports the outcome in the vocabulary
   * `InlineField` renders. Nothing here throws: the field needs an outcome.
   *
   * ⚠️ `''` becomes `null`, and `null` **CLEARS THE OVERRIDE** — it returns the
   * deal to FOLLOWING ITS COMPANY. It does not say the deal has no account
   * lead; there is no way to say that on a deal, only on a company. That is why
   * the messages below name the company's person rather than talking about an
   * empty field.
   *
   * The read-back is not the companies page's "an unknown key was stripped"
   * check — `PATCH /deals` is `.strict()`. It is here because the value SENT
   * and the value READ BACK are different fields: an id goes out, a resolved
   * person and a flag come back, and only re-reading them proves the override
   * took.
   */
  async function saveAccountLead(next: string): Promise<InlineSaveResult> {
    const ticket = accountLeadWriteSeq.current + 1;
    accountLeadWriteSeq.current = ticket;

    const body: DealUpdateInput = { accountLeadId: next === '' ? null : Number(next) };

    try {
      const response = await api.updateDeal(dealId, body);
      const saved = response.data;
      if (accountLeadWriteSeq.current === ticket) setDeal(saved);

      const applied =
        next === ''
          ? !saved.accountLeadIsOverride
          : saved.accountLeadIsOverride && String(saved.accountLead?.id ?? '') === next;
      if (!applied) {
        return {
          status: 'warning',
          message:
            'The server accepted it but the account lead is unchanged. Nothing was saved.',
        };
      }

      if (next === '') {
        return {
          status: 'saved',
          message: saved.accountLead
            ? `Saved. This deal follows its company again (${staffLabel(saved.accountLead)}).`
            : 'Saved. This deal follows its company, which names no account lead.',
        };
      }
      return {
        status: 'saved',
        message: `Saved. ${
          saved.accountLead ? staffLabel(saved.accountLead) : 'This person'
        } leads this deal, overriding the company’s.`,
      };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * A 403 carries no `details`, so it is never a field error — and it can
         * happen although `canUpdate` said otherwise, because a role can change
         * under a live token.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'deals') ??
              'Your account no longer has permission to change this deal.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This deal no longer exists, so the change could not be saved.',
          };
        }
        // A 422 names the request key, so it lands on the field that caused it.
        const fieldMessage = caught.fieldErrors.accountLeadId;
        if (fieldMessage) return { status: 'error', message: fieldMessage };
        // The `.strict()` rejection and the empty-PATCH refusal both carry an
        // EMPTY path, so they arrive here rather than on the field.
        const formMessage = caught.formMessage;
        if (formMessage) return { status: 'error', message: formMessage };
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  }

  /**
   * The account lead, edited where it is read.
   *
   * The control's VALUE is the override, never the resolved person: a deal that
   * is merely following its company stores nothing, so the picker is empty even
   * though a name is on screen beside it. Seeding it with the resolved value
   * would show a company's choice as though this deal had made it, and the
   * first save would freeze that person on as a real override nobody asked for.
   *
   * The empty row is therefore the REVERT, and it names who it restores —
   * "Follow the company (Daryna Kodinets)". Never "None" or "Unassigned":
   * clearing does not blank the field, and a label saying so would lie about
   * what the write does.
   *
   * No second revert button beside it: `InlineField` owns one status line per
   * save, and a button doing the same write from outside would report through a
   * second one.
   */
  function accountLeadField() {
    if (!deal) return null;
    /* The deal's OWN choice. `null` here means it is following the company. */
    const held = deal.accountLeadIsOverride ? deal.accountLead : null;
    const options: InlineFieldOption[] = leadStaff.users.map((row) => ({
      value: String(row.id),
      label: row.name,
      hint: row.email,
    }));
    /*
     * The override holder may sit outside the fetched page, or outside the
     * account-lead group. Losing them would make the control read as "follow
     * the company", and one stray save would clear an override nobody touched.
     * `outsideGroupNote` labels a kept non-member and is null while the list is
     * unknown.
     *
     * The COMPANY's lead is deliberately not pinned in as a selectable person:
     * the way to follow the company is the empty row, not picking the same
     * name, which would write an override that merely agrees today and stops
     * tracking tomorrow.
     */
    if (held && !leadStaff.users.some((row) => row.id === held.id)) {
      const outside = outsideGroupNote(leadStaff);
      options.unshift({
        value: String(held.id),
        label: held.name,
        hint: [held.email, outside].filter(Boolean).join(' · '),
      });
    }

    /* The one case where clearing really does leave nobody is a deal with no
       company: the row says so rather than promising a company that is not
       there. */
    const followLabel =
      deal.company === null
        ? 'No account lead · no company'
        : deal.companyAccountLead
          ? `Follow the company (${deal.companyAccountLead.name})`
          : 'Follow the company (names none)';

    return (
      <InlineField
        kind="picker"
        label="Account lead"
        value={held ? String(held.id) : ''}
        display={<AccountLeadValue deal={deal} />}
        disabled={!canUpdate}
        options={options}
        optionsLoading={usersLoading}
        emptyOptionLabel={followLabel}
        placeholder={usersLoading ? 'Loading users…' : followLabel}
        searchPlaceholder="Search staff…"
        {...(() => {
          /*
           * The group narrowing survives the 2026-09-05 helper-text sweep:
           * naming somebody outside `account_lead` is a live 422, so this
           * states a refusal. The truncation note beside it was removed.
           */
          const footnote = narrowingNote(leadStaff);
          return footnote ? { footnote } : {};
        })()}
        onSave={saveAccountLead}
      />
    );
  }

  async function removeLine() {
    if (!pendingLine) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.deleteDealProduct(dealId, pendingLine.id);
      setPendingLine(null);
      await loadLines();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        setPendingLine(null);
        await loadLines();
      } else {
        setActionError(caught instanceof Error ? caught.message : 'Could not remove the product');
      }
    } finally {
      setBusy(false);
    }
  }

  async function removeDeal() {
    setBusy(true);
    setActionError(null);
    try {
      await api.deleteDeal(dealId);
      navigate('/deals');
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) navigate('/deals');
      else setActionError(caught instanceof Error ? caught.message : 'Could not delete the deal');
      setBusy(false);
    }
  }

  /*
   * ⚠️ **`raiseInvoice()` AND `convertFailure()` ARE DELETED, and their absence
   * is the change rather than a casualty of it.** Both existed to drive and
   * report `api.raiseInvoiceForDeal` from a `ConfirmDialog` on this page. The
   * owner's decision of 2026-08-30 made deal→invoice an editable prefilled form,
   * so the call, the double-click ref, the spinner state and the refusal block
   * all live inside `InvoiceFormModal` now — with `submitLock`,
   * `dealConvertFailure` and the per-line 422 routing the dialog could never
   * have rendered.
   *
   * Keeping either here would leave a second implementation of a REFUSAL about
   * money, reachable from nothing, and read later as documentation of a path
   * that no longer exists. Two specifics worth not re-deriving:
   *
   *  - the `COMPANY_HAS_NO_TAX_RATE` block offered **a link to the company and
   *    never a retry**, because the identical request can only produce the
   *    identical refusal until somebody edits that record — and it took the id
   *    from the DEAL's own company, because the refusal also fires when the deal
   *    names none and the id is not in the prose;
   *  - the 403 branch named `'invoices'` outright, and still does, in the form.
   *
   * ⚠️ **`fromDeal` in router state is gone with it.** It was set by the
   * navigation this function performed and told `InvoiceDetailPage` to warn that
   * no tax rate came across — a claim that stopped being true when a converted
   * line began taking its rate from the company. The form's `onSaved` navigates
   * plainly; see `InvoiceDetailPage`, where the banner it fed was removed in the
   * same pass.
   */


  /*
   * The two figures the delete confirmation names, fetched together when it
   * opens. Either failing leaves both unknown, which `attachmentsSentence` says
   * plainly rather than half-reporting.
   *
   * ⚠️ The task figure is the LIST total, deliberately not `taskSummary`'s:
   * summary counts only what is still OPEN, and a closed task is destroyed by
   * this delete just the same. `perPage: 1` because only `meta.total` is read.
   *
   * ⚠️ **`systemCount` is typed as required and can still be absent at
   * RUNTIME** — a server predating it serves the row without the key. The
   * `typeof` test is what keeps that out of the sentence: an absent figure
   * reports NO automatic notes, so the dialog reads exactly as it did before
   * this field existed rather than printing `undefined`. ⛔ It is NOT a `?? 0`
   * over a value the server sent: a real `0` and an absent key are both "say
   * nothing about automatic notes" here, and only the absent one is a guess.
   */
  function loadAttachmentTotals() {
    setAttachmentTotals({ tasks: null, notes: null, systemNotes: 0 });
    void Promise.all([
      api.listTasks({ dealId, perPage: 1 }),
      api.noteSummary({ target: 'deal', ids: String(dealId) }),
    ])
      .then(([taskPage, notes]) => {
        const summary = notes.data[0] ?? null;
        setAttachmentTotals({
          tasks: taskPage.meta.total,
          notes: summary?.count ?? 0,
          systemNotes:
            summary !== null && typeof summary.systemCount === 'number' ? summary.systemCount : 0,
        });
      })
      .catch(() => setAttachmentTotals({ tasks: null, notes: null, systemNotes: 0 }));
  }

  const lineCount = lines?.length ?? 0;
  const quoteCount = quotesTotal ?? 0;
  const invoiceCount = invoicesTotal ?? 0;

  /*
   * ⚠️ **`alreadyRaised` IS DELETED WITH THE CONFIRMATION IT WAS WRITTEN FOR.**
   * It said what had already been raised against this deal — "2 invoices already
   * exist against this deal. Nothing refuses another." — because neither
   * endpoint answers a 409 and that count was the whole of what stood between a
   * reader and a duplicate. It only ever rendered inside the invoice
   * `ConfirmDialog`, which no longer exists.
   *
   * **The fact it carried has not gone away and is not lost: the Quotes and
   * Invoices panels below list what exists, on the same page, without anything
   * having to be opened.** That is strictly more than a count, which is why this
   * is a deletion rather than a move into the modal — a sentence inside a form
   * summarising a panel three screens up is a second copy that can disagree with
   * the list it summarises.
   *
   * ⚠️ Its one subtlety is worth keeping for whoever writes the next such
   * sentence: `quotesTotal` and `invoicesTotal` are **null on a failed read**,
   * and a `?? 0` there turns "we do not know" into a confident "none" — the
   * exact direction that gets somebody a duplicate.
   */

  /*
   * `deal` is the target TYPE (singular); `dealId` is the list FILTER key. The
   * drawer derives the second from the first, so only the singular goes in.
   */
  const activityTarget = { type: 'deal', id: dealId } as const;
  /* Never blank in practice, but the sheet's subheading is the only thing
     saying WHICH record it is about, so an empty name falls back to the id. */
  const activityLabel = deal.dealName.trim() || `Deal #${dealId}`;

  return (
    <>
      <PageHeader
        eyebrow={<BackToDeals />}
        title={deal.dealName}
        description={
          <>
            <DealStageBadge stage={deal.stage} />
            {deal.company && (
              <>
                <span className="text-fg-subtle">·</span>
                <LinkedValue to={`/companies/${deal.company.id}`} name={deal.company.name} />
              </>
            )}
            {deal.closingDate && (
              <>
                <span className="text-fg-subtle">·</span>
                <span>Closes {formatDateOnly(deal.closingDate)}</span>
              </>
            )}
          </>
        }
        actions={
          /*
            Tasks and notes lead, and are never gated: the task and note routers
            carry no role guard, so a `viewer` who can open this deal can write
            against it. The header wraps, so the row folds at 320px.
          */
          <>
            <ActivityButton target={activityTarget} onOpen={() => setActivityOpen(true)} />
            {/*
              * Never disabled "because a quote already exists". A deal is
              * legitimately quoted more than once — a revision, an alternative,
              * a second attempt after a rejection — and the panels below are how
              * somebody sees what has already been raised. The same holds for
              * invoices, where the server likewise refuses no repeat.
              *
              * Both labels say CONVERT rather than "New", because the basket
              * above is what crosses, and the word is the only place a reader
              * can see that before pressing.
              */}
            {canCreateQuote && (
              <Button
                variant="primary"
                /*
                 * ⚠️ Opens the prefilled form; it does NOT raise anything. The
                 * label still says CONVERT because that is still what the button
                 * begins — and because the word is the only place a reader can
                 * see that the basket above is what crosses.
                 */
                onClick={() => setConvertingToQuote(true)}
              >
                Convert to quote
              </Button>
            )}
            {canCreateInvoice && (
              <Button
                variant="secondary"
                /*
                 * ⚠️ Opens the prefilled form; it does NOT raise anything. Until
                 * 2026-08-30 this opened a confirmation whose one press created
                 * a real financial document, so the caution the label carried
                 * has moved into the form — where the person can also correct
                 * the figures BEFORE the invoice exists.
                 *
                 * ⚠️ **"Raise", not "Convert", and it matches the modal's own
                 * title and button.** A deal is not consumed by this:
                 * `POST /deals/{id}/invoices` answers no 409 and the same deal
                 * may be invoiced again, so "Convert" would describe a one-way
                 * change of state that does not happen.
                 */
                onClick={() => setRaisingInvoice(true)}
              >
                Raise invoice
              </Button>
            )}
            {canCreateDeliverables && lines !== null && lines.length > 0 && (
              /*
               * "All lines added" only from a links read that SUCCEEDED and names
               * every line — a failed read (`null`) leaves the button enabled.
               */
              deliverableLinks !== null &&
              lines.every((line) =>
                deliverableLinks.some((link) => link.dealProductId === line.id),
              ) ? (
                <Button variant="secondary" disabled>
                  All lines added
                </Button>
              ) : (
                <Button variant="secondary" onClick={() => setAddingDeliverables(true)}>
                  Add to deliverables
                </Button>
              )
            )}
            {canUpdate && (
              <Button variant="secondary" leadingIcon={<PencilIcon />} onClick={() => setEditing(true)}>
                Edit deal
              </Button>
            )}
            {canDelete && (
              <Button
                variant="danger"
                iconOnly
                leadingIcon={<TrashIcon />}
                aria-label={`Delete ${deal.dealName}`}
                title={`Delete ${deal.dealName}`}
                onClick={() => {
                  setActionError(null);
                  loadAttachmentTotals();
                  setPendingDeal(true);
                }}
              />
            )}
            <LayoutToolbar layout={layout} />
          </>
        }
      />

      {/*
        * `min-w-0` on each panel is load-bearing: a grid item defaults to
        * min-width:auto, so the basket's wide table widened the whole track
        * instead of scrolling inside its own TableWrap, and the page overflowed
        * below 768px.
        *
        * ⚠️ The basket's floor is no longer a `min-w-[760px]` literal — it is a
        * number computed from the visible columns, on the table's own `style`
        * (see `BASKET_COLUMN_WIDTHS`). It still reaches 760px with every column
        * shown, so this stays exactly as load-bearing as it was.
        */}
      <div className="grid gap-4">
        {/* The four panels are the movable sections; the amount-mismatch
            Notice under them stays fixed at the foot. */}
        <LayoutGrid
          layout={layout}
          render={{
            details: () => (
        <Panel className="min-w-0" aria-labelledby="deal-details-heading">
          <PanelHeader
            id="deal-details-heading"
            title="Deal details"
            description={`Created ${formatDateTime(deal.createdAt)}`}
          />

          {/*
            * The inline bank, arranged as `CompanyDetailPage` arranges its own:
            * the editable fields lead, the read-only and DERIVED ones follow.
            *
            * Keyed on the record, so opening a different deal cannot carry a
            * half-typed draft or a "Saved" line across to a deal it was never
            * about. A refresh of the SAME deal keeps the key, and therefore
            * keeps whatever the user is in the middle of.
            *
            * ⚠️ It owns name, amount, currency, stage, deal type, lead source,
            * closing date and next task — so those are not repeated as
            * read-only rows below. One value, one control: a field shown twice
            * can disagree with itself, and the read-only twin is the half that
            * goes stale.
            *
            * ⚠️ Description is NOT in that list any more — the owner removed
            * the control from this page's inline bank and from `DealFormModal`
            * on 2026-08-31. `deal.description` still reads back and still holds
            * real data on live deals; nothing here renders it, deliberately, so
            * do not add a read-only row "because the value is there".
            *
            * ⚠️ Probability is NOT in that list and has not been since the
            * override was retired (2026-08-31): a deal's percentage is its
            * STAGE's, so it is shown on the stage rather than edited as a field
            * of its own. `StageSwatch` renders it beside the stage name.
            *
            * ⚠️ Owner is NOT in that list and has not been since the picker was
            * retired: nothing on this page reads or writes `ownerId`. The
            * Relationship group's Account lead row is the replacement, and it is
            * a DIFFERENT person, not a rename.
            *
            * The Edit modal STAYS — inline is the fast path, the modal is better
            * for filling in many fields at once.
            *
            * `min-w-0` because a grid item defaults to min-width:auto, the same
            * sub-768px overflow the panels themselves guard against.
            */}
          {/*
            ⚠️ **`PanelBody`, not a bare `div`** — see the note on
            `QuoteDetailPage`. Without it these fields sit flush against the
            panel edge while the `PanelHeader` above carries `px-4 py-3`, so
            every label is outdented from its own heading. `PanelBody`'s default
            density is that same padding, which is why this is the primitive
            rather than a hand-written `px-4` that would then have to be kept in
            step with the header's.
          */}
          <PanelBody key={deal.id} className="min-w-0">
            <InlineDealFields deal={deal} onSaved={() => void loadDeal()} />
          </PanelBody>

          {/*
            * DERIVED, which is why it is not one of the inline fields above:
            * the deal's VALUE against the STAGE's probability, so it moves when
            * the stage does and there is nothing here for a person to type.
            *
            * ⚠️ The value is `dealValue` — the LINES' total whenever the deal
            * has lines, the typed amount only when it has none (owner's ruling,
            * 2026-09-10). So adding a product moves this figure without anybody
            * touching the Amount field above.
            */}
          <DetailGroup title="Forecast">
            <DetailItem label="Expected revenue">
              <Money value={expected} emptyLabel="Needs a value and a stage" />
              {/*
                * ⚠️ **Only when the deal has lines and NO typed amount.** That
                * is the one case where the figure a reader sees has no control
                * behind it: the Amount field above edits `deal.amount`, which
                * is empty, so without this line the forecast looks like it was
                * computed from nothing. When both exist the field shows what
                * the field writes and there is nothing surprising to caption.
                */}
              {valueFromLines && (
                <span className="mt-0.5 block text-xs text-fg-muted">
                  From the products below, not the Amount field.
                </span>
              )}
            </DetailItem>
            {/*
              * DERIVED by the server (owner 2026-10-27): the linked quote whose
              * current stage is accepted. `undefined` = an older API, so the row
              * is absent; `null` = not signed, `—`.
              */}
            {deal.quoteSigned !== undefined && (
              <DetailItem label={QUOTE_SIGNED_LABEL}>
                {deal.quoteSigned === null ? (
                  quoteSignedLabel(deal.quoteSigned)
                ) : (
                  <TextLink to={`/quotes/${deal.quoteSigned.quoteId}`}>
                    {quoteSignedLabel(deal.quoteSigned)}
                    {deal.quoteSigned.quoteNumber !== null &&
                      ` · ${deal.quoteSigned.quoteNumber}`}
                  </TextLink>
                )}
              </DetailItem>
            )}
          </DetailGroup>

          {/* Company and contact stay read-only here: neither is edited in
              place, and both are links rather than values. */}
          <DetailGroup title="Relationship">
            <DetailItem label="Company">
              <LinkedValue to={`/companies/${deal.company?.id}`} name={deal.company?.name} />
            </DetailItem>
            <DetailItem label="Contact">
              <LinkedValue to={`/contacts/${deal.contact?.id}`} name={deal.contact?.name} />
            </DetailItem>
            {/* Owner is gone from the deals surface: the column still holds
                whatever it held, but nothing here reads or writes it. */}
            <DetailItem label="Account lead">{accountLeadField()}</DetailItem>
            <CreditedTo creditedTo={deal.creditedTo} accountLeadId={deal.accountLead?.id} />
          </DetailGroup>

          <DetailGroup title="Project" columns="grid-cols-1">
            <DetailItem label="Project">
              {deal.project ? (
                <LinkedValue to={`/projects/${deal.project.id}`} name={deal.project.name} />
              ) : (
                /* Not phrased as a certainty: the deals module's projects
                   lookup cannot throw — on failure it logs and returns null, so
                   an unset project, a deleted one and an unreachable projects
                   table all arrive here identical. */
                <span className="text-fg-subtle">None, or the project was deleted</span>
              )}
            </DetailItem>
            <DetailItem label="Cities">
              {deal.cities.length > 0 ? (
                <>
                  <span className="flex flex-wrap gap-1">
                    {deal.cities.map((city) => (
                      <Badge key={city.id} tone="neutral" variant="outline">
                        {cityLabel(city)}
                      </Badge>
                    ))}
                  </span>
                  {/*
                    * ⚠️ These cities are DERIVED through the project on every
                    * request, never stored on the deal — so a closed deal can
                    * change what it reports. The sentence that said so was
                    * removed on the owner's stricter no-helper-text ruling
                    * (2026-09-05); nothing on screen distinguishes them from
                    * stored data now.
                    */}
                </>
              ) : (
                <span className="text-fg-subtle">
                  {deal.project
                    ? 'That project lists no cities.'
                    : 'No project, so no cities to read.'}
                </span>
              )}
            </DetailItem>
          </DetailGroup>

          {/*
            * ⚠️ A "Xero" group stood here, rendering `deal.xeroQuoteId`
            * read-only as "Quote reference typed on this deal". Removed on the
            * owner's decision, 2026-08-31, with the Quote ID box on
            * `DealFormModal`; the key left `DealCreateInput` at the same time,
            * so nothing in this app can write it any more.
            *
            * ⚠️ **The column and `Deal.xeroQuoteId` still exist and still read
            * back** — this hides a value, it deletes nothing. Restoring the row
            * needs only this markup, since the read type is untouched; adding
            * the WRITE back is a separate decision and would mean re-adding the
            * key to `DealCreateInput`, `FormValues` and `TEXT_KEYS`.
            *
            * The reason it was safe to drop stands recorded: THREE places can
            * hold a Xero quote reference and only `f_xero_links` is
            * authoritative. This column was one of the other two — a free-text
            * box a person typed, with nothing writing it and nothing keeping it
            * in step (`d_quotes.xero_quote_id` is the third) — so it could only
            * ever have somebody conclude a quote is in Xero because a deal had
            * a string in a box. Read the link through `linkOf`, never here.
            */}

          <PanelFooter>
            <p className="m-0 text-xs text-fg-muted">
              {/* modifiedBy is set to the creator at insert here, so it is not
                  an "edited" marker — the timestamps are. */}
              Last updated {formatDateTime(deal.updatedAt)}
              {deal.modifiedBy ? ` · ${deal.modifiedBy.name}` : ''}
            </p>
          </PanelFooter>
        </Panel>
            ),
            products: () => (
        <Panel className="min-w-0" aria-labelledby="deal-products-heading">
          <PanelHeader
            id="deal-products-heading"
            title="Products"
            description={
              linesLoading && lines === null
                ? 'Loading…'
                : `${lineCount} ${lineCount === 1 ? 'line' : 'lines'} on this deal`
            }
            actions={
              /*
               * ⚠️ **The wrapper is what lets these two WRAP at 320px.**
               * `PanelHeader`'s own actions slot is `flex shrink-0` with no
               * wrap, and below `sm` the header is a COLUMN — so that slot is
               * stretched to the panel's width while refusing to reflow, and
               * two controls side by side overflow it. A nested flex item
               * shrinks normally, so the pair falls onto a second line instead
               * of pushing the page sideways. No `justify-end`: with one button
               * this slot sat left below `sm`, and it still does.
               *
               * ⚠️ **NOT hidden below a breakpoint, unlike the catalog pages'
               * copy of this control.** They hide it below `md` because that is
               * where their table is replaced by a card list, and a column
               * control over a surface with no columns does nothing. This panel
               * has NO card variant — the table is the surface at every width,
               * and it is narrow viewports where hiding a column helps most.
               */
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <ColumnManager
                  layout={basketLayout}
                  labelOf={(key) => BASKET_LABELS[key] ?? key}
                  lockedKeys={BASKET_LOCKED_KEYS}
                  lockReasonOf={basketLockReason}
                  /* Actions only — see `BASKET_PINNED_KEYS`. Its freeze controls
                     are omitted; `product` keeps both of its, being locked and
                     not pinned. */
                  pinnedKeys={BASKET_PINNED_KEYS}
                />
                {canCreate && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<PlusIcon />}
                    onClick={() => setLineForm({})}
                  >
                    Add product
                  </Button>
                )}
              </div>
            }
          />

          {linesError && (
            <div className="border-b border-border px-4 py-3">
              <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load the products.</>}>
                <span className="[overflow-wrap:anywhere]">{linesError}</span>
              </Notice>
            </div>
          )}

          {/*
            🔴 **ONE MOUNT, TWO STATES — this was two separate `<Table>`s.** The
            skeleton and the data table now differ only in what follows the
            shared `<THead>`, and that is load-bearing rather than tidy:
            `useFrozenOffsets` measures the header ROW and re-measures through a
            `ResizeObserver` attached to it, so a header that unmounts and
            remounts when the lines land leaves the observer watching a detached
            row. Every frozen column's offset would stay at whatever the
            SKELETON measured — and the product column is fluid, so a skeleton
            bar and a real product name genuinely give different widths.
          */}
          {!(linesLoading && lines === null) && lineCount === 0 ? (
            <EmptyState
              icon={<DealIcon />}
              title="No products on this deal"
              {...(canCreate
                ? {
                    action: (
                      <Button variant="secondary" size="sm" onClick={() => setLineForm({})}>
                        Add the first product
                      </Button>
                    ),
                  }
                : {})}
            />
          ) : (
            <>
              <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
                {/* The floor is a number because it moves with the visible
                    columns — see `BASKET_COLUMN_WIDTHS`. It goes on the table's
                    own `style`; the `w-full` beside it still fills anything
                    wider. */}
                <Table style={{ minWidth: basketMinWidth }}>
                  <BasketHead
                    columnOrder={columnOrder}
                    showActions={showLineActions}
                    headRowRef={headRowRef}
                    frozen={frozen}
                  />
                  {linesLoading && lines === null ? (
                    <BasketSkeleton
                      columnOrder={columnOrder}
                      showActions={showLineActions}
                      frozen={frozen}
                    />
                  ) : (
                    <TBody>
                      {(lines ?? []).map((line) => (
                        <TR key={line.id}>
                          {columnOrder.map((key) =>
                            key === PRODUCT_KEY ? (
                              /*
                               * Product, project, cities and inclusions share
                               * ONE cell. The first two are the line's identity
                               * since 067 and the other two are what it
                               * promises; four more columns would push an
                               * already-wide table wider still on a page tested
                               * at 320px — which is also why this column is
                               * LOCKED: hiding it takes the project, the cities
                               * and the inclusions with it, and nothing else on
                               * this page shows them.
                               *
                               * Still the row header wherever it sits — the
                               * product is what identifies the line, not its
                               * position.
                               *
                               * ⚠️ **It takes the frozen props like every other
                               * cell, and it has to.** Freezing is a claim about
                               * a COLUMN: the header alone would stick while
                               * every product name scrolled out from under it,
                               * which reads as the freeze being broken rather
                               * than half-applied. `TH` switches to the BODY
                               * literals on `scope="row"`, so this does not paint
                               * as a heading — that was the objection that used
                               * to keep the column unfreezable.
                               */
                              <TH key={key} scope="row" {...frozenPropsFor(key, frozen)}>
                                <span className="[overflow-wrap:anywhere]">
                                  {line.product.productName}
                                </span>
                                {line.product.productCode && (
                                  <span className="mt-0.5 block text-xs text-fg-muted tabular">
                                    {line.product.productCode}
                                  </span>
                                )}
                                <LineProject project={line.project} />
                                <LineCities cities={line.cities} />
                                <LineInclusions inclusions={line.inclusions} />
                              </TH>
                            ) : key === ACTIONS_KEY ? (
                              <TD key={key} numeric pinned>
                                <div className="flex justify-end gap-1.5">
                                  {showLineActions ? (
                                    <>
                                      {canUpdate && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          iconOnly
                                          leadingIcon={<PencilIcon />}
                                          aria-label={`Edit ${lineLabel(line)}`}
                                          title={`Edit ${lineLabel(line)}`}
                                          onClick={() => setLineForm({ line })}
                                        />
                                      )}
                                      {canDelete && (
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          iconOnly
                                          leadingIcon={<TrashIcon />}
                                          aria-label={`Remove ${lineLabel(line)}`}
                                          title={`Remove ${lineLabel(line)}`}
                                          onClick={() => {
                                            setActionError(null);
                                            setPendingLine(line);
                                          }}
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-xs text-fg-subtle">—</span>
                                  )}
                                </div>
                              </TD>
                            ) : (
                              <TD
                                key={key}
                                numeric
                                className={basketCellClass(key)}
                                {...frozenPropsFor(key, frozen)}
                              >
                                <BasketValue column={key} line={line} />
                              </TD>
                            ),
                          )}
                        </TR>
                      ))}
                    </TBody>
                  )}
                </Table>
              </TableWrap>

              {/* The basket total belongs to the rows, so it waits for them —
                  a figure under a skeleton reads as a real total of nothing. */}
              {!(linesLoading && lines === null) && (
                <PanelFooter>
                  <p className="m-0 flex items-baseline gap-2 text-sm sm:ml-auto">
                    <span className="text-fg-muted">Basket total</span>
                    <span className="font-semibold text-fg">
                      <Money value={basketTotal} />
                    </span>
                    {deal.currency && (
                      <span className="text-xs text-fg-muted">{deal.currency}</span>
                    )}
                  </p>
                </PanelFooter>
              )}
            </>
          )}
        </Panel>
            ),
        /*
          Every quote raised from this deal, and there may legitimately be
          several: nothing refuses a repeat, so revisions, alternatives and a
          second attempt after a rejection all land here. A panel shaped like
          "the quote" would show the first and hide the rest — on exactly the
          deals where somebody is about to raise one more by accident.

          It filters on `dealId`, and the conversion writes that key onto the
          quote it creates. The link is a column on the QUOTE either way — a deal
          holds no pointer to what came off it.
        */
            quotes: () => (
        <Panel className="min-w-0" aria-labelledby="deal-quotes-heading">
          <PanelHeader
            id="deal-quotes-heading"
            title="Quotes raised from this deal"
            description={
              quotesLoading && quotes === null
                ? 'Loading…'
                : quotesError !== null
                  ? 'Couldn’t be loaded'
                  : pluralise(quoteCount, 'quote')
            }
          />

          {quotesError !== null && (
            <div className="border-b border-border px-4 py-3">
              <Notice
                tone="danger"
                role="alert"
                title={<>Couldn&rsquo;t load the quotes.</>}
                action={
                  <Button variant="secondary" size="sm" onClick={() => void loadQuotes()}>
                    Try again
                  </Button>
                }
              >
                <span className="[overflow-wrap:anywhere]">{quotesError}</span>
              </Notice>
            </div>
          )}

          {quotesLoading && quotes === null ? (
            <PanelBody>
              <div className="grid gap-2">
                {Array.from({ length: 2 }, (_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </PanelBody>
          ) : quotes === null ? null : quotes.length === 0 ? (
            <EmptyState
              icon={<QuoteIcon />}
              title="No quotes raised from this deal yet"
              {...(canCreateQuote
                ? {
                    /* The products DO cross: `POST /deals/{id}/convert` copies
                       each line's product, project, cities, inclusions,
                       quantity, price, discount and description in one
                       transaction. */
                    description: 'Convert to quote copies the products above onto a new quote.',
                  }
                : {})}
            />
          ) : (
            <>
              <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
                <Table className="min-w-[560px]">
                  <THead>
                    <TR>
                      <TH>Quote</TH>
                      <TH className="w-40">Stage</TH>
                      <TH numeric className="w-40">
                        Grand total
                      </TH>
                      <TH className="w-44">Raised</TH>
                      <TH className="w-12">
                        <span className="sr-only">Actions</span>
                      </TH>
                    </TR>
                  </THead>
                  <TBody>
                    {quotes.map((quote) => (
                      <TR key={quote.id}>
                        <TH scope="row">
                          <TextLink to={`/quotes/${quote.id}`} className="[overflow-wrap:anywhere]">
                            {quote.subject}
                          </TextLink>
                          {/* Genuinely nullable, so it is simply absent rather
                              than rendered as a gap where a reference belongs. */}
                          {quote.quoteNumber && (
                            <span className="mt-0.5 block text-xs text-fg-muted tabular">
                              {quote.quoteNumber}
                            </span>
                          )}
                        </TH>
                        <TD>
                          <StageBadge stage={quote.stage} />
                        </TD>
                        <TD numeric>
                          {/* Each quote carries its own currency, and these are
                              never summed into a panel total: a figure across
                              mixed currencies would be wrong by construction. */}
                          <SharedMoney amount={quote.grandTotal} currency={quote.currency} />
                        </TD>
                        <TD>{formatDateTime(quote.createdAt)}</TD>
                        <TD>
                          <PreviewQuotePdfButton
                            variant="row"
                            target={{
                              id: quote.id,
                              quoteNumber: quote.quoteNumber,
                              subject: quote.subject,
                              companyName: deal.company?.name ?? null,
                            }}
                            onPreview={quotePdfPreview.open}
                          />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>

            </>
          )}
        </Panel>
            ),
        /*
          The invoices raised from this deal — the quotes panel above with more
          at stake. `POST /deals/{id}/invoices` is repeatable and answers no 409,
          so this list is the only guard against a second real financial
          document.

          ⚠️ **No total is summed across these rows.** Each invoice carries its
          own currency and nothing in this system converts between them, so one
          figure over a mixed set would be wrong by construction. The count is
          the number that means something.
        */
            invoices: () => (
        <Panel className="min-w-0" aria-labelledby="deal-invoices-heading">
          <PanelHeader
            id="deal-invoices-heading"
            title="Invoices raised from this deal"
            description={
              invoicesLoading && invoices === null
                ? 'Loading…'
                : invoicesError !== null
                  ? 'Couldn’t be loaded'
                  : pluralise(invoiceCount, 'invoice')
            }
          />

          {invoicesError !== null && (
            <div className="border-b border-border px-4 py-3">
              <Notice
                tone="danger"
                role="alert"
                title={<>Couldn&rsquo;t load the invoices.</>}
                action={
                  <Button variant="secondary" size="sm" onClick={() => void loadInvoices()}>
                    Try again
                  </Button>
                }
              >
                <span className="[overflow-wrap:anywhere]">{invoicesError}</span>
              </Notice>
            </div>
          )}

          {invoicesLoading && invoices === null ? (
            <PanelBody>
              <div className="grid gap-2">
                {Array.from({ length: 2 }, (_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </PanelBody>
          ) : invoices === null ? null : invoices.length === 0 ? (
            <EmptyState
              icon={<InvoiceIcon />}
              title="No invoices raised from this deal yet"
              {...(canCreateInvoice
                ? {
                    description:
                      'Convert to invoice copies the products above onto a new Draft invoice.',
                  }
                : {})}
            />
          ) : (
            <>
              <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
                <Table className="min-w-[560px]">
                  <THead>
                    <TR>
                      <TH>Invoice</TH>
                      <TH className="w-40">Status</TH>
                      <TH numeric className="w-40">
                        Grand total
                      </TH>
                      <TH className="w-44">Raised</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {invoices.map((invoice) => (
                      <TR key={invoice.id}>
                        <TH scope="row">
                          <TextLink
                            to={`/invoices/${invoice.id}`}
                            className="[overflow-wrap:anywhere]"
                          >
                            {invoice.subject}
                          </TextLink>
                          {/* ⚠️ An invoice has no CRM-side number — the only
                              reference it carries is `xeroInvoiceNumber`, which
                              a conversion never writes, so it is null until a
                              push or a person records one. Absent rather than
                              rendered as a gap. */}
                          {invoice.xeroInvoiceNumber && (
                            <span className="mt-0.5 block text-xs text-fg-muted tabular">
                              {invoice.xeroInvoiceNumber}
                            </span>
                          )}
                        </TH>
                        <TD>
                          {/*
                            ⚠️ No `statuses` list is passed, so the badge reads
                            the row's own `isSettled` flag and never a name. A
                            converted invoice arrives on `Draft` — or on NO
                            status at all, because the server resolves that row
                            by name and logs a warning rather than failing the
                            call if an admin has renamed it.
                          */}
                          <StatusBadge status={invoice.status} />
                        </TD>
                        <TD numeric>
                          <InvoiceMoney value={invoice.grandTotal} currency={invoice.currency} />
                        </TD>
                        <TD>{formatDateTime(invoice.createdAt)}</TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>

            </>
          )}
        </Panel>
            ),
          }}
        />

        {/*
         * The basket total and the deal's own amount are independent columns —
         * nothing in the API keeps them in step — so a mismatch is worth
         * pointing out rather than silently showing two different numbers.
         *
         * ⚠️ **The ruling of 2026-09-10 makes this MORE worth saying, not
         * less.** The lines now win everywhere the deal's value is shown, so a
         * deal in this state reports the basket on the list, in the forecast
         * and on every dashboard, while the Amount field still holds — and
         * still edits — the other figure. The typed number has not been
         * overwritten and it is not what anybody is being shown, which is
         * exactly the pair of facts a person needs told.
         *
         * ⚠️ **The condition is unchanged, deliberately.** `deal.amount !==
         * null` keeps it quiet for a deal that was only ever priced by its
         * lines: there is no second figure there to disagree with, and the
         * forecast's own caption already says where the value came from.
         */}
        {basketTotal !== null && deal.amount !== null && basketTotal !== deal.amount && (
          <Notice tone="warning" className="min-w-0">
            This deal is valued at {formatDecimalString(basketTotal)} — its products. The
            Amount field still holds {formatDecimalString(deal.amount)}.
          </Notice>
        )}
      </div>

      {/* Tasks and notes are one button in the header away, in a sheet over the
          deal rather than as two panels at the foot of it, which is the one
          place nobody scrolls to. */}
      <ActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        target={activityTarget}
        recordLabel={activityLabel}
        viewerId={viewerId}
        viewerTimeZone={viewerTimeZone}
      />

      {/* Mounted only while open, so its ticks and outcomes reset per open. */}
      {addingDeliverables && lines !== null && (
        <AddToDeliverablesDialog
          onClose={() => setAddingDeliverables(false)}
          lines={lines}
          hasCompany={deal.company !== null}
          links={deliverableLinks}
          onSaved={() => void loadDeliverableLinks()}
        />
      )}

      <DealFormModal
        open={editing}
        onClose={() => setEditing(false)}
        deal={deal}
        /*
         * ⚠️ **THE LINE RELOAD IS NOT BELT-AND-BRACES — the Products panel below
         * is STALE without it.** That modal edits the basket through
         * `DealLineItemsTable`, and since 10 September 2026 its footer "Save
         * changes" is the only way those rows are written: the grid's own Save
         * button is gone. `setDeal` refreshes the deal RECORD, and the record
         * says nothing about its lines — `lines` is a separate read — so a save
         * that added a row left this page reporting "0 lines on this deal" over
         * a row the API had already stored.
         *
         * ⚠️ **It predates that change** (the grid's own Save had the same
         * effect, and `onLinesChanged` is deliberately not passed from the
         * modal) — but it was reachable only by pressing a control that has
         * since been removed, so it is now the ordinary path rather than a
         * corner.
         *
         * Unconditional, and the cost is one `GET` on a deal edit that touched
         * no line. Conditioning it would mean the modal reporting whether any
         * row moved — a second thing to keep true, to save a request nobody
         * waits on.
         */
        onSaved={(saved) => {
          setDeal(saved);
          void loadLines();
        }}
      />

      <LineItemFormModal
        open={lineForm !== null}
        onClose={() => setLineForm(null)}
        dealId={dealId}
        line={lineForm?.line}
        existingLines={lines ?? []}
        onSaved={() => void loadLines()}
      />

      {/*
        * ── Converting this deal to a QUOTE ─────────────────────────────────
        *
        * ⚠️ **NO ConfirmDialog in front of this**, and that is the decision
        * rather than an omission — see the note over the import. The form is
        * where the person reads what is about to happen and corrects it, and a
        * dialog would ask the question before they had seen the answer.
        *
        * ⚠️ **`convertFrom`, never `prefill`.** A prefilled create would post
        * `POST /quotes` and drop every line item on the deal: a line is
        * `POST /quotes/{id}/products` and cannot exist before the record does,
        * so only `POST /deals/{id}/convert` carries the basket across. The prop
        * is what selects that path.
        *
        * ⚠️ **`onSaved` navigates and does NOT reload the panels below.** Same
        * reasoning the invoice route gives: a page that stays put after raising
        * a document reads as though nothing happened. It fires on a completed
        * conversion AND on one whose header follow-up was refused — the quote is
        * real in both cases, and the second is exactly when somebody must be
        * taken to it rather than left on this page.
        *
        * ⚠️ **No `onXeroPush` — CORRECTED 2026-08-30, AND THE REASON CHANGED
        * COMPLETELY.** This read:
        *
        * > *"⚠️ **No `onXeroAutoPush`.** That callback runs only after an EDIT,
        * > and a quote one second old has no Xero counterpart — the only thing
        * > a push could do there is create one, which is not something this
        * > page may do on somebody's behalf. The quote's own page is where it
        * > is sent."*
        *
        * ⚠️ **The prop is `onXeroPush`, and it no longer CAUSES anything.** It
        * hands up the server's report of what the save already did; the push
        * lives inside the request. So "not something this page may do on
        * somebody's behalf" described a choice this page no longer has, and
        * anything reading it as a guard is reading a guard that is not there.
        *
        * **What still makes omitting it right, on facts that have nothing to do
        * with the prop's name:**
        *
        * - **A DEAL is never pushed to Xero.** There is no deal entity in the
        *   integration at all — quotes, invoices and companies are the three —
        *   so nothing this page saves has a Xero counterpart to report on.
        * - **This modal is in CREATE mode** (`convertFrom`, no `quote`), and a
        *   create does not call `onXeroPush` — see its own doc.
        * - **`onSaved` navigates away**, so there is no surface left on this
        *   page for a report to render on. ⚠️ **This is the load-bearing one**,
        *   and it is why the report cannot be wired here even if the prop were
        *   passed: state set on a page that is unmounting is a band nobody reads.
        *
        * 🔴 **ONE CLAUSE OF THIS WAS FALSIFIED IN THE OTHER REPO AND THE
        * CONCLUSION STAYED RIGHT, WHICH IS THE DANGEROUS SHAPE.** *SUPERSEDED:
        * "The server answers a skip for a quote that is not in Xero yet, and **a
        * save never creates one there**, which is the rule the old note was
        * reaching for and is now enforced server-side for every writer rather
        * than by this omission."*
        *
        * **A save DOES create in Xero** — owner's reversal, `xero-autopush.ts`,
        * 2026-08-31, taken in the change that removed the "Send to Xero" button.
        * So `POST /deals/{id}/convert` really does put a new QUOTE into SEED's
        * accounting system, and its `xeroPush` can come back `failed` — on which
        * branch the server writes nothing and re-reads nothing, so the new
        * quote's own page cannot tell that from a quote nobody has pushed yet.
        *
        * ✅ **THAT REPORT IS PAID — `QuoteFormModal` reads `xeroPush` off the
        * conversion and holds itself open on a `failed`.** *SUPERSEDED, the
        * standing debt: "⛔ **THAT REPORT IS THEREFORE OWED, AND IT IS OWED IN
        * `QuoteFormModal`, NOT HERE.**"* The call site stopped writing
        * `(await api.convertDeal(…)).data`, which had discarded the envelope's
        * second half at the expression.
        *
        * ⛔ **THE INSTRUCTION UNDER IT IS UNTOUCHED AND STILL BINDING: do not
        * close this by passing `onXeroPush` from this page.** The reason is the
        * one that decided where the report goes in the first place — the modal
        * is the only surface that outlives the request, because `onSaved` below
        * NAVIGATES, so state set here is a band nobody reads. That is a fact
        * about this page and it did not change when the debt was paid.
        *
        * ⚠️ **And the entity is the QUOTE.** `deals.controller` calls
        * `autoPushAndRefresh('quotes', quote.id, …)` on the document it created,
        * so `conversionPushReport('quotes', …)` is the call — its invoice sibling
        * one mount below reports on an INVOICE from the same deal.
        *
        * The two facts that never depended on any of this: **a DEAL is never
        * pushed to Xero** — there is no deal entity in the integration at all —
        * and the quote's own page is still where it is sent from.
      */}
      <QuoteFormModal
        open={convertingToQuote}
        onClose={() => setConvertingToQuote(false)}
        convertFrom={deal}
        onSaved={(created) => navigate(`/quotes/${created.id}`)}
        onCreatedPreview={(created) => navigate(quotePreviewHref(created.id))}
      />
      {quotePdfPreview.element}

      {/*
        * ── Raising an INVOICE from this deal ───────────────────────────────
        *
        * 🔴 **THIS WAS A `ConfirmDialog` AND IS NOW THE FORM — owner's decision,
        * 2026-08-30, the second half of the instruction that made deal→quote a
        * modal.** *SUPERSEDED, and every word of it read as settled: "THIS IS
        * THE INVOICE ROUTE ONLY … **Everything below is unchanged from when it
        * served both routes.** The invoice was not part of the decision, and a
        * document that demands money is not one to change on inference — so only
        * the dead quote branches of the ternaries have gone … `tone` is
        * `primary`, not `danger`: neither call destroys anything. What earns the
        * confirmation is that both are irreversible from here."*
        *
        * **The caution was right and the conclusion expired.** Not changing a
        * money route on inference is exactly why it stayed a dialog while the
        * quote became a form; the owner then said the same thing about this one,
        * which is the instruction the inference was standing in for.
        *
        * ⚠️ **What the dialog could not do, and is the whole gain:** it raised
        * the invoice on one press with **nothing editable**, so a wrong quantity
        * or price on a copied line had to be corrected afterwards, on a document
        * that already demanded money. `POST /deals/{id}/invoices` takes a `lines`
        * override now, so the figures are corrected BEFORE anything exists.
        *
        * ⚠️ **The drift the old note warned about is still real and is still
        * closed the same way**, only the constants moved: the form renders
        * `CONVERTED_INVOICE_LINE_TAX_RATE` and `INVOICE_CONVERSION_MOVES_THE_DEAL`
        * (which replaced `CONVERSION_LEAVES_THE_DEAL_ALONE` on 2026-10-18, when
        * the deal's stage began auto-moving to won) from `@/lib/deal-conversion`
        * rather than re-wording either.
        * `CONVERTED_LINES_HAVE_NO_TAX` — which this dialog rendered — is
        * **deleted**: a copied line takes its rate from the company now, and on
        * this route a zero is unreachable because the tax refusal fires first.
        *
        * ⚠️ **`hasLineItems` is deliberately NOT passed.** The form derives the
        * count from the deal's own lines while converting, and the prop's default
        * of `false` would hand the four header totals to the header on a
        * conversion whose lines are about to own them.
        */}
      <InvoiceFormModal
        open={raisingInvoice}
        onClose={() => setRaisingInvoice(false)}
        convertFromDeal={deal}
        /*
         * ⚠️ **No `fromDeal` router state, and its absence is a fix rather than
         * an omission.** The old navigation set it so `InvoiceDetailPage` could
         * warn that no tax rate came across — a claim that stopped being true
         * when a converted line began taking its rate from the company, and one
         * this route makes impossible, since the conversion is refused outright
         * when the company records none. The banner it fed is gone in the same
         * pass; re-adding the state would resurrect it.
         */
        /*
         * ⚠️ **No `onXeroPush`, and the modal is what reports the raise's Xero
         * half — 2026-09-13.** `POST /deals/{id}/invoices` pushes the INVOICE it
         * creates and answers `xeroPush` beside `data`; a `failed` there writes
         * nothing server-side, so the new invoice arrives with no Xero link and
         * its own page cannot tell that from an invoice nobody has pushed yet.
         *
         * `InvoiceFormModal` reads it and **holds itself open** on a refusal
         * rather than handing it up, which is the only arrangement that works
         * from here: `onSaved` navigates, so a band set on this page would be
         * unmounted before anybody read it. See `conversionPushReport`.
         *
         * ⛔ **Do not wire `onXeroPush` here to "finish" this.** It would fire on
         * the follow-up header PATCH only, set state on a page that is leaving,
         * and report the wrong write.
         */
        onSaved={(created) => navigate(`/invoices/${created.id}`)}
      />

      <ConfirmDialog
        open={pendingLine !== null}
        onClose={() => {
          setPendingLine(null);
          setActionError(null);
        }}
        onConfirm={() => void removeLine()}
        /* Named by product AND project: two rows can carry the same product
           under different projects now, so the product name alone would leave
           the reader guessing which of them is about to go. */
        title={pendingLine ? `Remove ${lineLabel(pendingLine)}?` : 'Remove product?'}
        description={
          <>
            <p className="m-0">Takes the line off this deal. The product itself is untouched.</p>
            {pendingLine && pendingLine.inclusions.length > 0 && (
              <p className="m-0 mt-2">
                Its {pluralise(pendingLine.inclusions.length, 'campaign inclusion')}{' '}
                {pendingLine.inclusions.length === 1 ? 'goes' : 'go'} too.
              </p>
            )}
          </>
        }
        confirmLabel="Remove product"
        loading={busy}
        error={actionError}
      />

      <ConfirmDialog
        open={pendingDeal}
        onClose={() => {
          setPendingDeal(false);
          setActionError(null);
        }}
        onConfirm={() => void removeDeal()}
        title={`Delete ${deal.dealName}?`}
        description={
          <>
            <p className="m-0">
              {lineCount === 0
                ? 'This deal has no products on it.'
                : `Its ${lineCount} ${lineCount === 1 ? 'product' : 'products'} go too.`}
            </p>
            {/* The tasks and notes cascade with the deal exactly as the basket
                does, and the API names neither — so they are named here. */}
            <p className="m-0 mt-2">
              {attachmentsSentence(
                attachmentTotals.tasks,
                attachmentTotals.notes,
                attachmentTotals.systemNotes,
              )}
            </p>
            <p className="m-0 mt-2">This cannot be undone.</p>
          </>
        }
        confirmLabel="Delete deal"
        loading={busy}
        error={actionError}
      />
    </>
  );
}

function BasketHead({
  columnOrder,
  showActions,
  headRowRef,
  frozen,
}: {
  columnOrder: readonly string[];
  showActions: boolean;
  /* `useFrozenOffsets` MEASURES this row: in an auto-layout table a `w-*` on a
     `<th>` is a request the content can outvote, so the rendered width of a
     column is knowable only from the DOM. */
  headRowRef: RefObject<HTMLTableRowElement | null>;
  frozen: FrozenModel;
}) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {columnOrder.map((key) => (
          <TH
            key={key}
            numeric={key !== PRODUCT_KEY}
            className={basketHeadClass(key, showActions)}
            {...(key === ACTIONS_KEY ? { pinned: true } : frozenPropsFor(key, frozen))}
          >
            {BASKET_LABELS[key] ?? key}
          </TH>
        ))}
      </TR>
    </THead>
  );
}
